/**
 * Pure, React-free helpers for the Buy-at-Target-Price product-selection
 * screen. The catalog metadata (Caption, MetalType, WeightCaption) comes from
 * the Algolia buy index; prices come from the `products/prices` API. These
 * functions merge the two and apply the screen's client-side filters, kept
 * separate from the component so they can be unit-tested in isolation.
 */

// The metal-chip values. 'all' means no metal filter.
export const METAL_FILTERS = {
  ALL: 'all',
  GOLD: 'gold',
  SILVER: 'silver',
};

// Merge an Algolia hit (display metadata) with its matching price record
// (Locations/prices) by ProductCode. Returns hits with any matching price
// fields spread on top, exactly as the Buy page does.
export const mergeHitsWithPrices = (hits = [], productPrices = []) => {
  const pricesByCode = new Map();
  productPrices.forEach((price) => {
    if (price && price.ProductCode) {
      pricesByCode.set(price.ProductCode, price);
    }
  });

  return hits.map((hit) => {
    const price = pricesByCode.get(hit.Code || hit.ProductCode);
    return price ? { ...hit, ...price } : hit;
  });
};

// Case-insensitive match of a product's metal type against the selected chip.
const matchesMetal = (product, metalFilter) => {
  if (!metalFilter || metalFilter === METAL_FILTERS.ALL) {
    return true;
  }
  return (product.MetalType || '').toLowerCase() === metalFilter.toLowerCase();
};

// Multi-select weight match. An empty selection means "all weights".
const matchesWeight = (product, weightFilters) => {
  if (!weightFilters || weightFilters.length === 0) {
    return true;
  }
  return weightFilters.includes(product.WeightCaption);
};

// Case-insensitive substring match against the product name.
const matchesSearch = (product, searchText) => {
  const term = (searchText || '').trim().toLowerCase();
  if (!term) {
    return true;
  }
  return (product.Caption || '').toLowerCase().includes(term);
};

// Apply all three filters. `products` are already merged hit+price objects.
export const filterProducts = (
  products = [],
  { metalFilter = METAL_FILTERS.ALL, weightFilters = [], searchText = '' } = {},
) =>
  products.filter(
    (product) =>
      matchesMetal(product, metalFilter) &&
      matchesWeight(product, weightFilters) &&
      matchesSearch(product, searchText),
  );

// Distinct weight captions present in the catalog, preserving first-seen order.
// Drives the weight-drawer checkbox list so it always reflects real inventory.
export const getAvailableWeights = (products = []) => {
  const seen = new Set();
  const weights = [];
  products.forEach((product) => {
    const weight = product.WeightCaption;
    if (weight && !seen.has(weight)) {
      seen.add(weight);
      weights.push(weight);
    }
  });
  return weights;
};

// Build the product thumbnail URL from the algolia imageCode template.
// `imageCode` is `config.algoliaSearch.imageCode`; `rootUrl` is settings.RootUrl.
export const buildProductImageSrc = (imageCode, rootUrl, productCode) => {
  if (!imageCode || !productCode) {
    return '';
  }
  return imageCode
    .replace('{product_code}', productCode)
    .replace('{base_url}', rootUrl || '');
};
